import { Injectable } from '@angular/core';
import { CACHE_EXPIRATION_SUBJECT, RequestCacheConfig, RequestCacheObject } from '../models/Request';
import { HttpEvent, HttpRequest } from '@angular/common/http';
import { interval, Observable, shareReplay, tap } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class RequestCacheService {
    private readonly requestCache = new Map<string, RequestCacheObject<HttpEvent<any>>>()
    private readonly cleanupTimerMs = 1 * 60 * 1000

    constructor() {
        // Cleanup expired requests every minute (in-flight requests are never removed here)
        interval(this.cleanupTimerMs).subscribe(() => {
            this.requestCache.forEach((request, key) => {
                if (this.isExpired(request)) {
                    this.requestCache.delete(key);
                }
            })
        })
    }

    checkForDuplicateRequest( method: string, url: string, body?: any ) {
        const requestCacheKey = this.getUniqueRequestKey(method, url, body);
        return this.getRequestFromCache(requestCacheKey);
    }

    /**
     * Shares the request between identical callers (same method + URL with params + body).
     * The entry lives while the request is in flight and, if the config has a timer, for that long after it completes.
     * Failed requests are never kept.
     */
    saveRequestToCache(request: HttpRequest<any>, requestEvent$: Observable<any>, cacheConfig: RequestCacheConfig = new RequestCacheConfig()): Observable<any> {
        const requestCacheKey = this.getUniqueRequestKey(request.method, request.urlWithParams, request.body);

        if (requestCacheKey === undefined) {
            return requestEvent$;
        }

        const persistenceMs = cacheConfig.persistenceTimerInMs;
        const entry: RequestCacheObject<HttpEvent<any>> = { observable: requestEvent$, expiresAt: undefined, isExpiredSubject: request.context.get(CACHE_EXPIRATION_SUBJECT) };

        entry.observable = requestEvent$.pipe(
            tap({
                complete: () => {
                    if (persistenceMs > 0) {
                        entry.expiresAt = Date.now() + persistenceMs;
                    } else {
                        this.removeEntry(requestCacheKey, entry);
                    }
                },
                error: () => this.removeEntry(requestCacheKey, entry)
            }),
            shareReplay({bufferSize: 1, refCount: false})
        )

        this.requestCache.set(requestCacheKey, entry);

        return entry.observable;
    }

    /** Returns undefined for bodies that can't be serialized reliably (FormData, Blob, ...), meaning "don't cache". */
    getUniqueRequestKey(method: string, url: string, body?: any): string | undefined {
        if (body instanceof FormData || body instanceof Blob || body instanceof ArrayBuffer || body instanceof URLSearchParams) {
            return undefined;
        }

        return `${method}:${url}:${JSON.stringify(body ?? null)}`;
    }

    getRequestFromCache(key?: string): RequestCacheObject<HttpEvent<any>> | undefined {
        if (String.isNullOrUndefinedOrWhitespace(key)) {
            return undefined;
        }

        const request = this.requestCache.get(key!);

        if (request && this.isExpired(request)) {
            this.requestCache.delete(key!);
            return undefined;
        }

        return request;
    }

    private isExpired(request: RequestCacheObject<HttpEvent<any>>): boolean {
        const timedOut = request.expiresAt !== undefined && request.expiresAt <= Date.now();
        return timedOut || !!request.isExpiredSubject?.value;
    }

    /** Only removes the entry if it hasn't been replaced by a newer request under the same key. */
    private removeEntry(key: string, entry: RequestCacheObject<HttpEvent<any>>) {
        if (this.requestCache.get(key) === entry) {
            this.requestCache.delete(key);
        }
    }
}
